import { type AppDispatch, type AppThunk } from "features/Application/globaltypes/redux";
import DataService from "features/Application/services/dataServices/typedDataService";
import {
  fetchBegin,
  fetchFailure,
  fetchSuccess,
  uploadBegin,
  uploadSuccess,
  uploadCancel,
  uploadProcessingSuccess,
  uploadProcessingFailure,
} from "../slices/DetailsSlice";
import { openEntityDetails, setEntityId } from "../slices/EntityStateSlice";
import fileUtils, { type AcceptCancelationHandler, type OnUploadProgressHandler } from "../../../../../utils/fileUtils";
import { type ScormFileRequest } from "../../services/types";
import { deleteTask, updateTask } from "../../../../BackgroundTasks/state/backgroundTasksActions";
import scormTasks from "../../../../BackgroundTasks/scormTasks";

export const fetchItemAction =
  (id: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(fetchBegin());
      const response = await DataService.scorm.getScorm(id);
      const item = response.data;
      dispatch(fetchSuccess(item));
      dispatch(
        openEntityDetails({
          id: item.id,
          isDraft: item.isDraft,
          dateModified: item.dateModified,
        }),
      );
      dispatch(setEntityId(id));
    } catch (_) {
      dispatch(fetchFailure());
    }
  };

export const uploadFileAction =
  (
    url: string,
    file: File,
    onUploadProgressHandler: OnUploadProgressHandler,
    acceptCancellationHandler: AcceptCancelationHandler,
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(uploadBegin());
    try {
      await fileUtils.uploadFileToBlob(url, file, onUploadProgressHandler, acceptCancellationHandler, null);
      dispatch(uploadSuccess());
    } catch (error) {
      dispatch(uploadCancel());
      throw error;
    }
  };

export const updateFileAction = (data: ScormFileRequest) => async (dispatch: AppDispatch) => {
  dispatch(uploadBegin());
  try {
    await DataService.scorm.updateFile(data);
    dispatch(uploadSuccess());
  } catch (error) {
    dispatch(uploadCancel());
    throw error;
  }
};

export const updateProcessingAction = (payload: { id: number; title: string }) => async (dispatch: AppDispatch) => {
  try {
    const task = scormTasks.getReadyToPublish(payload.id, (taskId: number) => dispatch(deleteTask(taskId)));
    dispatch(updateTask(payload.id, task));
    dispatch(uploadProcessingSuccess());
  } catch (error) {
    dispatch(uploadCancel());
    throw error;
  }
};

export const updateProcessingFailureAction =
  (payload: { id: number; errorMessage: string }) => async (dispatch: AppDispatch) => {
    try {
      const task = scormTasks.getFailed(payload.id, (taskId: number) => dispatch(deleteTask(taskId)));
      dispatch(updateTask(payload.id, task));
      dispatch(uploadProcessingFailure(payload.errorMessage));
    } catch (error) {
      dispatch(uploadCancel());
      throw error;
    }
  };

export const uploadThumbnailAction = (id: number, fileKey: string) => DataService.scorm.updateThumbnail(id, fileKey);
